import React, { useState, useMemo, useEffect } from 'react';
import { Button } from './button';
import { Maximize2, ShoppingCart, Lock, Unlock, Eye, X, Loader, Loader2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from './dialog';
import MapPurchaseSection from './mappurchuase';

const RecentMaps = ({
  recentMaps,
  onEnlarge,
  handleSizeSelect,
  isUnlocking,
  setVoucher,
  selectedFormat,
  isPortrait,
  setSelectedTab,
  cacheStatus
}) => {
  const mostRecentIndex = useMemo(() => {
    if (!recentMaps || recentMaps.length === 0) return -1;
    const timestamps = recentMaps.map(map => new Date(map.created_at).getTime());
    return timestamps.indexOf(Math.max(...timestamps));
  }, [recentMaps]);

  const [selectedIndex, setSelectedIndex] = useState(mostRecentIndex);
  const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);
  const [showCacheStatus, setShowCacheStatus] = useState(true);

  useEffect(() => {
    setSelectedIndex(mostRecentIndex);
  }, [mostRecentIndex]);

  if (!recentMaps || recentMaps.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto space-y-6">
        {showCacheStatus && cacheStatus && (
          <div className="relative w-full bg-white p-4 mb-4 rounded-xl shadow-xl">
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2"
              onClick={() => setShowCacheStatus(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            <EnhancedCacheStatus status={cacheStatus} />
          </div>
        )}
        <div className="w-full max-w-4xl mx-auto aspect-[4/3] bg-gray-100 rounded-lg flex items-center justify-center">
          <p className="text-gray-400">No maps generated yet</p>
        </div>
      </div>
    );
  }
  const validSelectedIndex = selectedIndex >= 0 && selectedIndex < recentMaps.length
    ? selectedIndex
    : 0;
  const selectedMap = recentMaps[validSelectedIndex];
  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return new Intl.DateTimeFormat('default', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Date unavailable';
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      {/* Featured Image Section */}
      {/* <div className={`relative group bg-gray-50 rounded-lg overflow-hidden ${
        selectedIndex === mostRecentIndex ? 'ring-2 ring-red-500' : ''
      }`}> */}
      {showCacheStatus && cacheStatus && (
        <div className="relative w-full bg-white rounded-xl shadow-xl p-4 mb-4">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2"
            onClick={() => setShowCacheStatus(false)}
          >
            <X className="h-4 w-4" />
          </Button>
          <EnhancedCacheStatus status={cacheStatus} />
        </div>
      )}

      <div className={'relative group flex bg-gray-200 rounded-xl p-4 overflow-hidden items-center justify-center'}>
        <img
          src={selectedMap.imageUrl}
          alt={`Generated map ${selectedMap.id}`}
          className={'max-h-[40vh] object-scale-down shadow-2xl shadow-black/50'}
        />

        {selectedIndex === mostRecentIndex && (
          <div className="absolute top-2 left-2 bg-red-500 text-white px-3 py-1 rounded-xl text-sm font-medium">
            Latest
          </div>
        )}

        {/* Lock Status */}
        <div className="absolute top-2 right-2 bg-gray-900/75 text-white px-3 py-1 rounded-xl text-sm font-medium flex items-center gap-2">
          {selectedMap.locked ? (
            <>
              <Lock className="w-4 h-4" />
              <span>Locked</span>
            </>
          ) : (
            <>
              <Unlock className="w-4 h-4" />
              <span>Unlocked</span>
            </>
          )}
        </div>

        {/* Enlarge Button */}
        <div className="absolute bottom-2 right-2 opacity-100 group-hover:opacity-100 transition-opacity">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="default"
                  size="icon"
                  className="h-8 w-8 bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl"
                  onClick={() => onEnlarge({ imageUrl: selectedMap.imageUrl })}
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>View larger image</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="absolute bottom-2 left-2 bg-gray-900/75 text-white px-3 py-1 rounded-xl text-sm">
          {formatDate(selectedMap.created_at)}
        </div>
      </div>

      {/* Purchase Button */}
      <div className="flex justify-center">
        {selectedMap.locked ? (
          <Dialog open={isPurchaseOpen} onOpenChange={setIsPurchaseOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Unlock Map
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full max-w-lg items-center justify-center">
              <MapPurchaseSection
                finalImage={selectedMap.imageUrl}
                maxDpis={selectedMap.max_dpis}
                selectedFormat={selectedMap.format || selectedFormat}
                onSizeSelect={(size, dimensions, needsRegeneration, voucherCode) => {
                  handleSizeSelect(selectedMap.id, size, dimensions, needsRegeneration, voucherCode);
                  setIsPurchaseOpen(false);
                }}
                isUnlocking={isUnlocking}
                isPortrait={selectedMap.isPortrait ?? isPortrait}
                km2={selectedMap.km2}
                voucher={setVoucher}
              />
            </DialogContent>
          </Dialog>
        ) : (
          <Button
            className="bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl"
            onClick={() => setSelectedTab('history')}
          >
            <Eye className="mr-2 h-5 w-5" />
            View and Download
          </Button>
        )}
      </div>

      {/* Thumbnail Grid */}
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {recentMaps.map((map, index) => (
          <div
            key={map.id}
            className={`relative cursor-pointer group bg-gray-50 rounded-lg overflow-hidden transition-all duration-200 ${index === selectedIndex
              ? 'ring-2 ring-blue-500 scale-100'
              : 'hover:ring-2 hover:ring-gray-300 hover:scale-102'
              } ${index === mostRecentIndex
                ? 'ring-2 ring-red-500'
                : ''
              }`}
            onClick={() => setSelectedIndex(index)}
          >
            <div className="aspect-[4/3] overflow-hidden relative">
              <img
                src={map.imageUrl}
                alt={`Generated map ${map.id}`}
                className={'w-full h-full object-cover'}
              />
              {map.locked && (
                <Lock className="absolute top-1 right-1 w-4 h-4 text-gray-900/75" />
              )}
            </div>
            {index === mostRecentIndex && (
              <div className="absolute top-1 left-1 bg-red-500 text-white px-2 py-0.5 rounded text-xs font-medium">
                Latest
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-gray-900/75 text-white px-2 py-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
              {formatDate(map.created_at)}
            </div>
          </div>
        ))}
      </div>
      {recentMaps.length > 0 && (
        <p className='text-center'>Last five generated maps</p>
      )}

    </div>
  );
};


const EnhancedCacheStatus = ({ status }) => {
  if (!status) return null;
  const { total_cells, cached_cells, cells_to_fetch, cells_info } = status;
  const cachePercentage = Math.round((cached_cells / total_cells) * 100);

  const componentNames = {
    building: "buildings",
    forests: "forests",
    naturalareas: "natural areas",
    water_areas: "water areas",
    streets_roads: "streets (roads only)",
    streets_all: "streets (detailed)",
  };

  const getStatusMessage = () => {
    if (cells_to_fetch === 0) return 'Everything cached!';
    if (cells_to_fetch === total_cells) return 'First time generating this area for your settings';
    return 'Some parts are missing for this area';
  };

  const statusColor = cells_to_fetch === 0 ? 'text-green-500' : 'text-yellow-500';
  const glowColor = cells_to_fetch === 0
    ? 'drop-shadow-[0_0_6px_rgba(34,197,94,0.6)]'
    : 'drop-shadow-[0_0_6px_rgba(234,179,8,0.6)]';

  const estimatedMinutes = cells_to_fetch === 0
    ? calculateEstimatedTime(cells_info, total_cells, true)
    : calculateEstimatedTime(cells_info, cells_to_fetch, false);

  return (
    // <div className="absolute inset-0 flex flex-col items-center justify-center p-4 bg-gray-300/40 text-black">
    //   <div className={`text-2xl text-center font-semibold mb-2 ${statusColor} ${glowColor}`}>
    //     {getStatusMessage()}
    //   </div>
    <div className="flex flex-col items-center justify-center p-1">
      <div className={`text-xl text-center font-semibold mb-1 ${statusColor} ${glowColor}`}>
        {getStatusMessage()}
      </div>
      <div className="text-sm mb-1">
        {cachePercentage}% Complete ({cached_cells}/{total_cells} cells)
      </div>

      {/* {cells_info && Object.entries(cells_info)
        .filter(([_, count]) => count > 0)
        .map(([component, count]) => (
          <div key={component} className="text-sm opacity-80">
            {count} cells need {componentNames[component] || component}
          </div>
        ))
      } */}

      <div className="text-md font-semibold mt-2 text-black">
        {cells_to_fetch > 0 ? 'This might take a bit' : 'Generation will be quick'}
      </div>

      {/* <div className="text-sm mt-1 opacity-80">
        Estimated time: {formatTime(estimatedMinutes)}
      </div> */}
      <Loader2 className="h-6 w-6 animate-spin" />
      {/* <ScannerEffect /> */}
    </div>
  );
};


const LoadingRaster = ({ total_cells, cells_to_fetch }) => {
  const rows = Math.ceil(Math.sqrt(total_cells * 0.75));
  const columns = Math.ceil(total_cells / rows);
  const cells = Array(total_cells).fill(null);

  return (
    <div className="absolute inset-4 grid gap-1 relative"
      style={{
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridAutoRows: '1fr',
      }}
    >
      {cells.map((_, index) => {
        const isLoading = index < cells_to_fetch;
        return (
          <div
            key={index}
            className={`w-full h-full rounded-lg ${isLoading ? 'bg-yellow-500/20' : 'bg-green-500/20'
              } flex items-center justify-center`}
          >
            {isLoading && (
              <Loader className="w-1/3 h-1/3 animate-spin opacity-50 text-yellow-500" />
            )}
          </div>
        );
      })}
    </div>
  );
};

const calculateEstimatedTime = (cells_info, total_cells, isCached = false) => {
  let totalWeight = 0;

  // Base time calculation per cell (2 minutes for 8 cells)
  const baseTimePerCell = 0.15; // 2 minutes / 8 cells = 0.25 minutes per cell

  if (!isCached) {
    // Determine streets weight first as it affects building weight
    const hasStreetsAll = cells_info.streets_all > 0;
    const hasStreetsRoads = cells_info.streets_roads > 0;

    // Add streets weight
    if (hasStreetsAll) {
      totalWeight += cells_info.streets_all * 0.20;
    } else if (hasStreetsRoads) {
      totalWeight += cells_info.streets_roads * 0.10;
    }

    // Calculate building weight based on streets presence
    if (cells_info.building > 0) {
      if (hasStreetsAll) {
        totalWeight += cells_info.building * 0.15;
      } else if (hasStreetsRoads) {
        totalWeight += cells_info.building * 0.2;
      } else {
        totalWeight += cells_info.building * 0.3;
      }
    }

    // Add other components
    if (cells_info.forests > 0) totalWeight += cells_info.forests * 0.05;
    if (cells_info.naturalareas > 0) totalWeight += cells_info.naturalareas * 0.05;
    if (cells_info.water_areas > 0) totalWeight += cells_info.water_areas * 0.05;
  }

  // Calculate total time based on cell count and weights
  let totalTime = total_cells * baseTimePerCell + totalWeight;

  // If cached, reduce time by 80%
  if (isCached) {
    totalTime = totalTime * 0.1;
  }

  return Math.max(0.5, Math.ceil(totalTime * 2) / 2); // Round to nearest 0.5, minimum 0.5
};

const formatTime = (minutes) => {
  if (minutes < 1) {
    return `< ${minutes * 60} seconds`;
  }
  if (minutes === 1) {
    return '< 1 minute';
  }
  if (Number.isInteger(minutes)) {
    return `< ${minutes} minutes`;
  }
  return `< ${minutes.toFixed(1)} minutes`;
};


// const DebugControls = ({ cacheStatus, setCacheStatus }) => {
//   const updateCellsInfo = (component, value) => {
//     setCacheStatus(prev => ({
//       ...prev,
//       cells_info: {
//         ...prev.cells_info,
//         [component]: parseInt(value) || 0
//       }
//     }));
//   };

//   return (
//     <div className="space-y-4 bg-gray-100 p-4 rounded-lg">
//       <div className="space-y-2">
//         <div>
//           <label className="block text-sm font-medium text-gray-700">Total Cells</label>
//           <input
//             type="number"
//             value={cacheStatus.total_cells}
//             onChange={(e) => setCacheStatus(prev => ({
//               ...prev,
//               total_cells: parseInt(e.target.value) || 0
//             }))}
//             className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
//           />
//         </div>
//         <div>
//           <label className="block text-sm font-medium text-gray-700">Cells to Fetch</label>
//           <input
//             type="number"
//             value={cacheStatus.cells_to_fetch}
//             onChange={(e) => setCacheStatus(prev => ({
//               ...prev,
//               cells_to_fetch: parseInt(e.target.value) || 0
//             }))}
//             className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
//           />
//         </div>
//       </div>

//       <div className="space-y-2">
//         <h3 className="font-medium text-sm text-gray-700">Cells Info</h3>
//         {Object.keys(cacheStatus.cells_info).map(component => (
//           <div key={component}>
//             <label className="block text-xs text-gray-600">{component}</label>
//             <input
//               type="number"
//               value={cacheStatus.cells_info[component]}
//               onChange={(e) => updateCellsInfo(component, e.target.value)}
//               className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
//             />
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

// const DebugMapLayout = () => {
//   const [cacheStatus, setCacheStatus] = useState({
//     cached_cells: 2,
//     cells_info: {
//       building: 2,
//       forests: 1,
//       naturalareas: 0,
//       streets_roads: 2,
//       streets_all: 0,
//       water_areas: 1
//     },
//     cells_to_fetch: 4,
//     total_cells: 12
//   });

//   return (
//     <div className="w-full max-w-4xl mx-auto p-4 space-y-8">
//       <div className="relative w-full aspect-[4/3] bg-white rounded-lg overflow-hidden">
//         <LoadingRaster
//           total_cells={cacheStatus.total_cells}
//           cells_to_fetch={cacheStatus.cells_to_fetch}
//         />
//         <EnhancedCacheStatus status={cacheStatus} />
//       </div>

//       <DebugControls
//         cacheStatus={cacheStatus}
//         setCacheStatus={setCacheStatus}
//       />
//     </div>
//   );
// };

export default RecentMaps;